import React, { useState, useEffect } from 'react';
import { Upload, User, MapPin, FileText, CheckCircle, Image } from 'lucide-react';
import '../styles/cultural-platform.css';

const Submissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    artworkTitle: '',
    category: 'arts',
    description: '',
    imageUrl: ''
  });
  const [submitted, setSubmitted] = useState(false);

  // Load submissions from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('psp_submissions');
    if (saved) {
      setSubmissions(JSON.parse(saved));
    } else {
      // Default showcase submissions
      const defaultSubmissions = [
        {
          id: 1,
          name: 'Maria Santos',
          country: 'Brazil',
          artworkTitle: 'Colors of Rio',
          category: 'arts',
          description: 'A vibrant oil painting celebrating the colorful streets and culture of Rio de Janeiro during Carnival season.',
          imageUrl: 'https://images.unsplash.com/photo-1735839550903-98b86765a0d2',
          date: '2025-12-01'
        },
        {
          id: 2,
          name: 'Raj Patel',
          country: 'India',
          artworkTitle: 'Monsoon Dreams',
          category: 'films',
          description: 'A short documentary exploring the lives of farmers during the monsoon season in rural Maharashtra.',
          imageUrl: 'https://images.unsplash.com/photo-1605368504600-f2cca6ea012a',
          date: '2025-11-28'
        },
        {
          id: 3,
          name: 'Yuki Tanaka',
          country: 'Japan',
          artworkTitle: 'Cherry Blossom Ceremony',
          category: 'culture',
          description: 'A photographic series capturing the traditional hanami (flower viewing) ceremonies across Kyoto.',
          imageUrl: 'https://images.unsplash.com/photo-1533669955142-6a73332af4db',
          date: '2025-11-25'
        },
        {
          id: 4,
          name: 'Ahmed Hassan',
          country: 'Egypt',
          artworkTitle: 'Echoes of the Pharaohs',
          category: 'arts',
          description: 'Digital art piece combining ancient Egyptian hieroglyphics with modern geometric patterns.',
          imageUrl: 'https://images.unsplash.com/photo-1678038592327-c5730737f867',
          date: '2025-11-20'
        },
        {
          id: 5,
          name: 'Sophie Martin',
          country: 'France',
          artworkTitle: 'Parisian Lights',
          category: 'films',
          description: 'A romantic short film showcasing the magic of Paris through the eyes of street musicians.',
          imageUrl: 'https://images.unsplash.com/photo-1743364971078-53fd19ea1033',
          date: '2025-11-15'
        },
        {
          id: 6,
          name: 'Chen Wei',
          country: 'Singapore',
          artworkTitle: 'Lion City Heritage',
          category: 'culture',
          description: 'A multimedia installation celebrating Singapore\'s multicultural identity through food, music, and dance.',
          imageUrl: 'https://images.unsplash.com/photo-1646828697863-4db40583627d',
          date: '2025-11-10'
        }
      ];
      setSubmissions(defaultSubmissions);
      localStorage.setItem('psp_submissions', JSON.stringify(defaultSubmissions));
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newSubmission = {
      ...formData,
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      imageUrl: formData.imageUrl || 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119'
    };
    
    const updatedSubmissions = [newSubmission, ...submissions];
    setSubmissions(updatedSubmissions);
    localStorage.setItem('psp_submissions', JSON.stringify(updatedSubmissions));
    
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setShowForm(false);
      setFormData({
        name: '',
        country: '',
        artworkTitle: '',
        category: 'arts',
        description: '',
        imageUrl: ''
      });
    }, 3000);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'arts': return '#E91E63';
      case 'films': return '#FF9800';
      case 'culture': return '#4CAF50';
      default: return '#1A73E8';
    }
  };

  return (
    <div className="submissions-page" data-testid="submissions-page">
      {/* Hero Section */}
      <section className="submissions-hero" style={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%)',
        padding: '120px 24px 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <div style={{ position: 'relative', zIndex: 10, maxWidth: '900px', margin: '0 auto' }}>
          <h1 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: '400',
            color: '#FFFFFF',
            marginBottom: '20px',
            animation: 'fadeInUp 0.8s ease-out'
          }}>
            Global Art Exhibition
          </h1>
          <p style={{
            fontSize: '18px',
            color: 'rgba(255,255,255,0.8)',
            lineHeight: '1.7',
            marginBottom: '32px',
            animation: 'fadeInUp 0.8s ease-out 0.2s both'
          }}>
            A showcase of creativity from artists, filmmakers, and cultural creators around the world.
            <br />Submit your work and join the global cultural movement.
          </p>
          <button
            onClick={() => setShowForm(true)}
            data-testid="submit-artwork-btn"
            style={{
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #E91E63 0%, #FF5722 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '50px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'fadeInUp 0.8s ease-out 0.4s both, pulse 2s infinite 1.5s'
            }}
          >
            <Upload size={20} />
            Submit Your Artwork
          </button>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      </section>

      {/* Submission Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            animation: 'slideUp 0.4s ease-out'
          }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <CheckCircle size={64} color="#4CAF50" style={{ marginBottom: '20px' }} />
                <h3 style={{ fontSize: '24px', marginBottom: '12px', color: '#202124' }}>
                  Submission Received!
                </h3>
                <p style={{ color: '#5F6368' }}>
                  Your artwork has been added to the exhibition.
                </p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: '500', color: '#202124' }}>
                    Submit Your Artwork
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#5F6368'
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#202124' }}>
                      <User size={18} /> Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      data-testid="submission-name"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#202124' }}>
                      <MapPin size={18} /> Country *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      data-testid="submission-country"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#202124' }}>
                      <FileText size={18} /> Artwork Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.artworkTitle}
                      onChange={(e) => setFormData({...formData, artworkTitle: e.target.value})}
                      data-testid="submission-title"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#202124' }}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      data-testid="submission-category"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        background: '#FFFFFF'
                      }}
                    >
                      <option value="arts">Arts</option>
                      <option value="films">Films</option>
                      <option value="culture">Culture</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#202124' }}>
                      <Image size={18} /> Image URL (optional)
                    </label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                      placeholder="https://example.com/your-image.jpg"
                      data-testid="submission-image"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#202124' }}>
                      Description *
                    </label>
                    <textarea
                      required
                      rows="4"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      data-testid="submission-description"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-form-btn"
                    style={{
                      width: '100%',
                      padding: '14px 24px',
                      background: 'linear-gradient(135deg, #1A73E8 0%, #1557B0 100%)',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Submit Artwork
                  </button>
                </form>
              </>
            )}
          </div>
          
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(50px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      {/* Exhibition Gallery */}
      <section style={{ background: '#FAFAFA', padding: '80px 24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '400',
              color: '#202124',
              marginBottom: '12px'
            }}>
              Featured Submissions
            </h2>
            <p style={{ color: '#5F6368', fontSize: '16px' }}>
              {submissions.length} artworks from creators worldwide
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {submissions.map((submission, index) => (
              <div
                key={submission.id}
                className="submission-card"
                data-testid={`submission-${index}`}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s ease',
                  animation: `cardFadeIn 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                <div style={{
                  position: 'relative',
                  height: '240px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={submission.imageUrl}
                    alt={submission.artworkTitle}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    padding: '6px 14px',
                    background: getCategoryColor(submission.category),
                    color: '#FFFFFF',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    textTransform: 'uppercase'
                  }}>
                    {submission.category}
                  </div>
                </div>
                <div style={{ padding: '24px' }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '500',
                    color: '#202124',
                    marginBottom: '8px'
                  }}>
                    {submission.artworkTitle}
                  </h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    color: '#5F6368',
                    fontSize: '14px'
                  }}>
                    <User size={16} />
                    <span>{submission.name}</span>
                    <span>•</span>
                    <MapPin size={16} />
                    <span>{submission.country}</span>
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: '#5F6368',
                    lineHeight: '1.6'
                  }}>
                    {submission.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes cardFadeIn {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .submission-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 40px rgba(0,0,0,0.15);
          }
          .submission-card:hover img {
            transform: scale(1.1);
          }
        `}</style>
      </section>
    </div>
  );
};

export default Submissions;
